/**
 * AppLayout Component
 *
 * Main layout wrapper providing consistent structure:
 * - Header with logo, environment selector, user menu
 * - Resizable sidebar with file tree
 * - Main content area (editor or form)
 * - Collapsible validation panel
 * - Status bar
 *
 * Uses React Router Outlet for nested routing support.
 */

import type { ReactElement, ReactNode } from "react";
import { useState, useCallback } from "react";
import { Outlet, useLocation } from "react-router-dom";

import type { PanelState } from "../../types";

import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { StatusBar } from "./StatusBar";

interface AppLayoutProps {
  readonly children?: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps): ReactElement {
  const location = useLocation();
  const isAdminSection = location.pathname.startsWith("/admin");

  const [panels, setPanels] = useState<PanelState>({
    sidebar: true,
    validation: true,
    terminal: false,
  });

  const togglePanel = useCallback((panel: keyof PanelState) => {
    setPanels((prev) => ({ ...prev, [panel]: !prev[panel] }));
  }, []);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Header */}
      <Header />

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - only show for non-admin sections */}
        {panels.sidebar && !isAdminSection && <Sidebar onToggle={() => togglePanel("sidebar")} />}

        {/* Main Editor/Content Area */}
        <main className="flex flex-1 flex-col overflow-hidden">{children ?? <Outlet />}</main>
      </div>

      {/* Status Bar - only show for non-admin sections */}
      {!isAdminSection && (
        <StatusBar sidebarVisible={panels.sidebar} onToggleSidebar={() => togglePanel("sidebar")} />
      )}
    </div>
  );
}

export default AppLayout;
